/**
 * Translations.
 *
 * Arabic is the source of truth (spec §15.1); English is the fallback. Every
 * key in `ar` must exist in `en` — `t()` returns the Arabic string rather than
 * the raw key when English is missing, so a gap degrades to a readable label
 * instead of leaking `dashboard.title` into the UI.
 */

import type { ErrorCode, Locale } from '@retailpro/shared';

const ar = {
  'app.name': 'ريتايل برو',
  'app.tagline': 'نظام نقاط البيع وإدارة الأعمال',
  'app.by': 'من تطوير',

  'nav.dashboard': 'لوحة التحكم',
  'nav.overview': 'نظرة عامة',
  'nav.items': 'الأصناف',
  'nav.branches': 'الفروع',
  'nav.eta': 'الضرائب الإلكترونية',
  'nav.comingSoon': 'قريباً',

  'login.title': 'تسجيل الدخول',
  'login.subtitle': 'أدخل بياناتك للوصول إلى النظام',
  'login.identifier': 'اسم المستخدم أو رقم الهاتف',
  'login.password': 'كلمة المرور',
  'login.pin': 'الرقم السري',
  'login.usePassword': 'استخدام كلمة المرور',
  'login.usePin': 'استخدام الرقم السري',
  'login.submit': 'دخول',
  'login.submitting': 'جارٍ الدخول...',
  'login.logout': 'تسجيل الخروج',

  // Change password. Arabic is the primary language, so this screen must be
  // usable without an English fallback: an operator forced off the seeded
  // password who cannot read the instructions cannot comply with them.
  'password.title': 'اختيار كلمة مرور جديدة',
  'password.titleVoluntary': 'تغيير كلمة المرور',
  'password.forced':
    'هذا الحساب لا يزال يستخدم كلمة المرور التي أُنشئ بها. اختر كلمة مرور خاصة بك قبل المتابعة — الميزات التجارية غير متاحة حتى تفعل ذلك.',
  'password.current': 'كلمة المرور الحالية',
  'password.new': 'كلمة المرور الجديدة',
  'password.confirm': 'أعد كتابة كلمة المرور الجديدة',
  'password.hint': '‏١٢ حرفًا على الأقل، مع حرف إنجليزي كبير وحرف صغير ورقم.',
  'password.submit': 'تغيير كلمة المرور',
  'password.submitting': 'جارٍ الحفظ...',
  'password.genericError': 'تعذّر تغيير كلمة المرور. حاول مرة أخرى.',

  'error.VALIDATION_FAILED': 'البيانات المدخلة غير صحيحة',
  'error.UNAUTHENTICATED': 'يجب تسجيل الدخول',
  'error.INVALID_CREDENTIALS': 'اسم المستخدم أو كلمة المرور غير صحيحة',
  'error.FORBIDDEN': 'لا تملك صلاحية للقيام بهذا الإجراء',
  'error.NOT_FOUND': 'العنصر المطلوب غير موجود',
  'error.CONFLICT': 'تعارض في البيانات',
  'error.PRECONDITION_FAILED': 'تم استيفاء شرط مسبق غير متاح',
  'error.PAYLOAD_TOO_LARGE': 'حجم البيانات كبير جداً',
  'error.UNSUPPORTED_MEDIA_TYPE': 'نوع المحتوى غير مدعوم',
  'error.RATE_LIMITED': 'عدد المحاولات كبير، حاول لاحقاً',
  'error.VERSION_CONFLICT': 'تم تعديل هذا السجل من مكان آخر، أعد تحميل الصفحة',
  'error.PERIOD_CLOSED': 'الفترة المحاسبية مغلقة',
  'error.STOCK_INSUFFICIENT': 'الكمية غير متوفرة في المخزون',
  'error.CREDIT_LIMIT_EXCEEDED': 'تم تجاوز حد الائتمان',
  'error.APPROVAL_REQUIRED': 'يتطلب هذا الإجراء موافقة',
  'error.IDEMPOTENCY_MISMATCH': 'تم استخدام مفتاح العملية بمحتوى مختلف',
  'error.INTERNAL_ERROR': 'حدث خطأ في النظام',
  'error.SERVICE_UNAVAILABLE': 'الخدمة غير متاحة مؤقتاً',
  'error.NETWORK_ERROR': 'تعذر الاتصال بالخادم',
  'error.UNEXPECTED_RESPONSE': 'استجابة غير متوقعة من الخادم',

  'common.retry': 'إعادة المحاولة',
  'common.loading': 'جارٍ التحميل...',
  'common.language': 'English',
  'common.theme': 'المظهر',
  'common.theme.light': 'فاتح',
  'common.theme.dark': 'داكن',
  'common.correlationId': 'رقم التتبع',
  'common.branches': 'الفروع',
  'common.noBranches': 'لا توجد فروع مسجلة',
  'common.branchesUnavailable': 'تعذر تحميل الفروع',
  'common.loggedInAs': 'مسجل الدخول باسم',
  'notFound.title': 'الصفحة غير موجودة',
  'notFound.body': 'الرابط الذي طلبته غير متاح.',
  'notFound.back': 'العودة للوحة التحكم',
} as const;

const en: Partial<Record<keyof typeof ar, string>> = {
  'app.name': 'RetailPro',
  'app.tagline': 'Point of Sale and business management',
  'app.by': 'Developed by',

  'nav.dashboard': 'Dashboard',
  'nav.overview': 'Overview',
  'nav.items': 'Items',
  'nav.branches': 'Branches',
  'nav.eta': 'E-Invoicing',
  'nav.comingSoon': 'Coming soon',

  'login.title': 'Sign in',
  'login.subtitle': 'Enter your credentials to access the system',
  'login.identifier': 'Username or phone number',
  'login.password': 'Password',
  'login.pin': 'PIN',
  'login.usePassword': 'Use password',
  'login.usePin': 'Use PIN',
  'login.submit': 'Sign in',
  'login.submitting': 'Signing in...',
  'login.logout': 'Sign out',

  'password.title': 'Choose a new password',
  'password.titleVoluntary': 'Change your password',
  'password.forced':
    'This account still uses the password it was created with. Choose your own before continuing — business features are unavailable until you do.',
  'password.current': 'Current password',
  'password.new': 'New password',
  'password.confirm': 'Repeat new password',
  'password.hint': 'At least 12 characters, with an uppercase letter, a lowercase letter and a digit.',
  'password.submit': 'Change password',
  'password.submitting': 'Saving...',
  'password.genericError': 'Could not change the password. Try again.',

  'error.VALIDATION_FAILED': 'The submitted data is invalid',
  'error.UNAUTHENTICATED': 'You need to sign in',
  'error.INVALID_CREDENTIALS': 'Incorrect username or password',
  'error.FORBIDDEN': 'You do not have permission for this action',
  'error.NOT_FOUND': 'The requested record was not found',
  'error.CONFLICT': 'Conflicting data',
  'error.PRECONDITION_FAILED': 'A precondition was not met',
  'error.PAYLOAD_TOO_LARGE': 'Payload too large',
  'error.UNSUPPORTED_MEDIA_TYPE': 'Unsupported media type',
  'error.RATE_LIMITED': 'Too many attempts, try again later',
  'error.VERSION_CONFLICT': 'This record changed elsewhere, reload the page',
  'error.PERIOD_CLOSED': 'The accounting period is closed',
  'error.STOCK_INSUFFICIENT': 'Insufficient stock',
  'error.CREDIT_LIMIT_EXCEEDED': 'Credit limit exceeded',
  'error.APPROVAL_REQUIRED': 'This action needs approval',
  'error.IDEMPOTENCY_MISMATCH': 'That operation key was reused with a different payload',
  'error.INTERNAL_ERROR': 'Something went wrong',
  'error.SERVICE_UNAVAILABLE': 'Service temporarily unavailable',
  'error.NETWORK_ERROR': 'Could not reach the server',
  'error.UNEXPECTED_RESPONSE': 'Unexpected response from the server',

  'common.retry': 'Try again',
  'common.loading': 'Loading...',
  'common.language': 'العربية',
  'common.theme': 'Theme',
  'common.theme.light': 'Light',
  'common.theme.dark': 'Dark',
  'common.correlationId': 'Reference',
  'common.branches': 'Branches',
  'common.noBranches': 'No branches registered',
  'common.branchesUnavailable': 'Could not load branches',
  'common.loggedInAs': 'Signed in as',
  'notFound.title': 'Page not found',
  'notFound.body': 'The link you requested is not available.',
  'notFound.back': 'Back to dashboard',
};

export type MessageKey = keyof typeof ar;

export function translate(locale: Locale, key: MessageKey): string {
  if (locale === 'en') return en[key] ?? ar[key];
  return ar[key];
}

/**
 * Map a stable error code to a sentence a cashier can act on.
 *
 * Codes the API can add later have no translation yet; the caller falls back to
 * the raw `detail` for those rather than showing nothing.
 */
export function translateError(locale: Locale, code: string): string | null {
  const key = `error.${code}` as MessageKey;
  return key in ar ? translate(locale, key) : null;
}

export function formatNumber(locale: Locale, value: number | string): string {
  const numeric = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(numeric)) return String(value);
  return new Intl.NumberFormat(locale === 'ar' ? 'ar-EG' : 'en-EG', {
    maximumFractionDigits: 2,
  }).format(numeric);
}

export function formatCurrency(locale: Locale, value: string | number): string {
  const numeric = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(numeric)) return String(value);
  return new Intl.NumberFormat(locale === 'ar' ? 'ar-EG' : 'en-EG', {
    style: 'currency',
    currency: 'EGP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numeric);
}

export type { ErrorCode };
